import React, { useState } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Paper,
  InputAdornment,
  IconButton,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useLocation } from 'react-router-dom';
import identityService from '../../services/identityService';
import LogoDark from '../../images/logo-rentbeet.png';
import '../IdentityLogin/styles.css';

function IdentityRegister() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const identityUrl = process.env.REACT_APP_IDENTITY_URL || 'https://auth.rentbeets.com';

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;
    setLoading(true);

    try {
      const registrationData = {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
      };

      const registrationId = searchParams.get('RegistrationId');
      const propertyId = searchParams.get('PropertyId');
      const unitId = searchParams.get('UnitId');

      if (registrationId) registrationData.registrationId = registrationId;
      if (propertyId) registrationData.propertyId = propertyId;
      if (unitId) registrationData.unitId = unitId;

      const response = await identityService.register(registrationData);

      if (response.success) {
        setSuccess(true);
      } else {
        setError(response.message || 'Registration failed');
      }
    } catch (err) {
      console.error('Registration error:', err);

      if (err.response?.status === 409) {
        setError('An account with this email already exists');
      } else if (
        err.code === 'ECONNREFUSED' ||
        err.message?.includes('Network Error') ||
        !err.response
      ) {
        setError('Unable to connect to the identity service. Please ensure it is running.');
      } else if (err.response?.status) {
        setError(
          `Registration failed (${err.response.status}): ${
            err.response.data?.message || err.message || 'Unknown error'
          }`
        );
      } else {
        setError(`An error occurred: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  /** ----------------------------------------------------------------------
   *  LOGO HEADER — Guaranteed centered
   * ---------------------------------------------------------------------- */
  const LogoHeader = (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center', 
        alignItems: 'center',
        marginBottom: 3,
        padding: 0,
      }}
    >
      <Link
        href="/"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-end',
          justifyContent: 'center',
          textDecoration: 'none',
          padding: 0,
          margin: 0,
          gap: 0,
        }}
      >
        <Box
          component="img"
          src={LogoDark}
          alt="RentBeets"
          sx={{
            width: '25px',
            height: 'auto',
            objectFit: 'contain',
            cursor: 'pointer',
            display: 'block',
            marginBottom: '3px',
          }}
        />
        <Typography
          sx={{
            fontFamily: 'Bitter, serif',
            fontSize: '20px',
            fontWeight: 700,
            color: '#8B3040',
            lineHeight: 1,
            margin: 0,
            padding: 0,
          }}
        >
          RentBeets
        </Typography>
      </Link>
    </Box>
  );

  /** SUCCESS SCREEN ----------------------------------------------- */
  if (success) {
    return (
      <div className="identity-page">
        <Container maxWidth="sm">
          <Box
            sx={{
              marginTop: 8,
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Paper
              elevation={3}
              sx={{
                padding: 4,
                width: '100%',
                maxWidth: '600px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {LogoHeader}

              <Alert severity="success" sx={{ marginBottom: 3 }}>
                Registration successful! Please check your email to confirm your account.
              </Alert>

              <Typography variant="body1" align="center" sx={{ marginBottom: 3 }}>
                We have sent an email to <strong>{formData.email}</strong>. Please click the link to activate your account.
              </Typography>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  href={`${identityUrl}/identity/login${window.location.search}`}
                  variant="body2"
                  sx={{ color: '#8B3040', fontWeight: 'bold' }}
                >
                  Return to Sign In
                </Link>
              </Box>
            </Paper>
          </Box>
        </Container>
      </div>
    );
  }

  /** REGISTER FORM ------------------------------------------------- */
  return (
    <div className="identity-page">
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              width: '100%',
              maxWidth: '600px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {LogoHeader}

            <Typography component="h2" variant="h5" align="center" sx={{ marginBottom: 3 }}>
              Create Account
            </Typography>

            {error && <Alert severity="error" sx={{ marginBottom: 2 }}>{error}</Alert>}

            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
              {['firstName', 'lastName', 'email'].map((field) => (
                <TextField
                  key={field}
                  margin="normal"
                  required
                  fullWidth
                  id={field}
                  label={
                    field === 'email'
                      ? 'Email Address'
                      : field === 'firstName'
                      ? 'First Name'
                      : 'Last Name'
                  }
                  name={field}
                  autoComplete={field}
                  value={formData[field]}
                  onChange={handleChange}
                  disabled={loading}
                  error={!!validationErrors[field]}
                  helperText={validationErrors[field]}
                />
              ))}

              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
                error={!!validationErrors.password}
                helperText={validationErrors.password || 'Minimum 8 characters'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={loading}
                error={!!validationErrors.confirmPassword}
                helperText={validationErrors.confirmPassword}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  backgroundColor: '#8B3040',
                  '&:hover': {
                    backgroundColor: '#6d2533',
                  },
                }}
              >
                {loading ? 'Creating Account...' : 'Sign Up'}
              </Button>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2">
                  Already have an account?{' '}
                  <Link
                    href={`${identityUrl}/identity/login${window.location.search}`}
                    variant="body2"
                    sx={{ color: '#8B3040', fontWeight: 'bold' }}
                  >
                    Sign in
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </div>
  );
}

export default IdentityRegister;
